import React from 'react';
import './ShiftSummary.css';

const computeTotalShifts = (schedule, residents) => {
  const totalShifts = residents.reduce((acc, resident) => ({ ...acc, [resident.name]: 0 }), {});
  Object.values(schedule).forEach(shift => {
    if (shift && shift !== 'empty') {
      totalShifts[shift] = (totalShifts[shift] || 0) + 1;
    }
  });
  return totalShifts;
};

const computeAverageDaysBetweenCalls = (schedule, residents, schedulerMode) => {
  const avgDaysBetweenCalls = {};
  residents.forEach(resident => {
    const residentDates = Object.keys(schedule)
      .filter(date => schedule[date] === resident.name)
      .map(date => new Date(date));
    if (residentDates.length < 2) {
      avgDaysBetweenCalls[resident.name] = null;
    } else {
      const daysBetweenCalls = residentDates
        .sort((a, b) => a - b)
        .reduce((acc, date, i, arr) => {
          if (i === 0) return acc;
          return acc.concat((date - arr[i - 1]) / (1000 * 60 * 60 * 24));
        }, []);
      avgDaysBetweenCalls[resident.name] = daysBetweenCalls.reduce((a, b) => a + b) / daysBetweenCalls.length;
    }
  });
  return avgDaysBetweenCalls;
};

const computeActual = (totalDays, totalShifts, schedulerMode) => {
  if (schedulerMode === 'resident' || schedulerMode === 'weekend') {
    return totalShifts > 0 ? `1 in ${(totalDays / totalShifts).toFixed(2)}` : 'N/A';
  } else {
    return totalShifts > 0 ? `Every ${((totalDays / 7) / totalShifts).toFixed(2)} weeks` : 'N/A';
  }
};

const computeShiftsPerDayOfWeek = (schedule, residents) => {
  const shiftsPerDayOfWeek = residents.reduce((acc, resident) => {
    acc[resident.name] = {
      Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    };
    return acc;
  }, {});

  Object.entries(schedule).forEach(([date, shift]) => {
    if (shift && shift !== 'empty') {
      const [year, month, day] = date.split('-');
      const dateObj = new Date(year, month - 1, day);
      const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
      if (shiftsPerDayOfWeek[shift]) {
        shiftsPerDayOfWeek[shift][dayOfWeek]++;
      }
    }
  });

  return shiftsPerDayOfWeek;
};

const ShiftSummary = ({ schedule, residents, startDate, endDate, schedulerMode, specialShifts }) => {
  const totalShifts = computeTotalShifts(schedule, residents);
  const avgDaysBetweenCalls = computeAverageDaysBetweenCalls(schedule, residents, schedulerMode);
  const shiftsPerDayOfWeek = computeShiftsPerDayOfWeek(schedule, residents);

  const totalDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  return (
    <div className="shift-summary">
      <h2>Shift Summary</h2>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>{schedulerMode === 'resident' || schedulerMode === 'weekend' ? 'Total Shifts' : 'Total Weeks'}</th>
            <th>Target</th>
            <th>Actual</th>
            <th>{schedulerMode === 'resident' || schedulerMode === 'weekend' ? 'Days Between Calls' : 'Weeks Between Calls'}</th>
            {schedulerMode === 'weekend' && (
              <>
                <th>Weekend Shifts</th>
                <th>Weekday Shifts</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
        {residents.map(resident => (
          <tr key={resident.name}>
            <td data-label="Name">{resident.name}</td>
            <td data-label={schedulerMode === 'resident' || schedulerMode === 'weekend' ? 'Total Shifts' : 'Total Weeks'}>
              {schedulerMode === 'resident' || schedulerMode === 'weekend' ? totalShifts[resident.name] : Math.floor(totalShifts[resident.name] / 7)}
            </td>
            <td data-label="Target">
              {schedulerMode === 'resident' || schedulerMode === 'weekend' 
                ? `1 in ${resident.max1inX}` 
                : resident.max1inX ? `Every ${resident.max1inX} weeks` : 'N/A'}
            </td>
            <td data-label="Actual">{computeActual(totalDays, totalShifts[resident.name], schedulerMode)}</td>
            <td data-label={schedulerMode === 'resident' || schedulerMode === 'weekend' ? 'Days Between Calls' : 'Weeks Between Calls'}>
              {avgDaysBetweenCalls[resident.name] !== null 
                ? (schedulerMode === 'resident' || schedulerMode === 'weekend' 
                    ? avgDaysBetweenCalls[resident.name].toFixed(2) 
                    : (avgDaysBetweenCalls[resident.name] / 7).toFixed(2))
                : 'N/A'}
            </td>
            {schedulerMode === 'weekend' && (
              <>
                <td data-label="Weekend Shifts">{specialShifts[resident.name]?.weekend || 0}</td>
                <td data-label="Weekday Shifts">{specialShifts[resident.name]?.weekday || 0}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
      </table>
      <h2>Shifts Per Day of Week</h2>
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Mon</th>
            <th>Tue</th>
            <th>Wed</th>
            <th>Thu</th>
            <th>Fri</th>
            <th>Sat</th>
            <th>Sun</th>
          </tr>
        </thead>
        <tbody>
          {residents.map(resident => (
            <tr key={resident.name}>
              <td data-label="Name">{resident.name}</td>
              <td data-label="Mon">{shiftsPerDayOfWeek[resident.name].Mon}</td>
              <td data-label="Tue">{shiftsPerDayOfWeek[resident.name].Tue}</td>
              <td data-label="Wed">{shiftsPerDayOfWeek[resident.name].Wed}</td>
              <td data-label="Thu">{shiftsPerDayOfWeek[resident.name].Thu}</td>
              <td data-label="Fri">{shiftsPerDayOfWeek[resident.name].Fri}</td>
              <td data-label="Sat">{shiftsPerDayOfWeek[resident.name].Sat}</td>
              <td data-label="Sun">{shiftsPerDayOfWeek[resident.name].Sun}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ShiftSummary;